import { useEffect, useState, useRef, useCallback } from 'react'
import { type DragEndEvent } from '@dnd-kit/core'

export interface Position {
  x: number
  y: number
}

export interface AllElementPositions {
  namePosition?: Position
  descriptionPosition?: Position
  sponsorLogoPosition: Position
  linkPosition?: Position
}

export interface UseBannerDragDropProps {
  initialNamePosition?: Position
  initialDescriptionPosition?: Position
  initialSponsorLogoPosition?: Position
  initialLinkPosition?: Position
  defaultNamePosition: Position
  defaultDescriptionPosition: Position
  defaultSponsorLogoPosition: Position
  defaultLinkPosition: Position
  onPositionChange?: (positions: AllElementPositions) => void
}

// Helper function to convert percentage positions to pixel positions for rendering
const percentageToPixels = (
  percentage: Position,
  containerWidth: number,
  containerHeight: number
): Position => {
  return {
    x: (percentage.x / 100) * containerWidth,
    y: (percentage.y / 100) * containerHeight,
  }
}

export const useBannerDragDrop = ({
  initialNamePosition,
  initialDescriptionPosition,
  initialSponsorLogoPosition,
  initialLinkPosition,
  defaultNamePosition,
  defaultDescriptionPosition,
  defaultSponsorLogoPosition,
  defaultLinkPosition,
  onPositionChange,
}: UseBannerDragDropProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [containerDimensions, setContainerDimensions] = useState({
    width: 0,
    height: 0,
  })

  const [namePosition, setNamePosition] = useState<Position>(
    initialNamePosition || defaultNamePosition
  )
  const [descriptionPosition, setDescriptionPosition] = useState<Position>(
    initialDescriptionPosition || defaultDescriptionPosition
  )
  const [sponsorLogoPosition, setSponsorLogoPosition] = useState<Position>(
    initialSponsorLogoPosition || defaultSponsorLogoPosition
  )
  const [linkPosition, setLinkPosition] = useState<Position>(
    initialLinkPosition || defaultLinkPosition
  )

  useEffect(() => {
    if (initialNamePosition) {
      setNamePosition(initialNamePosition)
    }
    if (initialDescriptionPosition) {
      setDescriptionPosition(initialDescriptionPosition)
    }
    if (initialSponsorLogoPosition) {
      setSponsorLogoPosition(initialSponsorLogoPosition)
    }
    if (initialLinkPosition) {
      setLinkPosition(initialLinkPosition)
    }
  }, [
    initialNamePosition,
    initialDescriptionPosition,
    initialSponsorLogoPosition,
    initialLinkPosition,
  ])

  // Update container dimensions on mount and resize
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const { clientWidth, clientHeight } = containerRef.current

        setContainerDimensions({ width: clientWidth, height: clientHeight })
      }
    }

    updateDimensions()

    const resizeObserver = new ResizeObserver(updateDimensions)
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current)
    }

    return () => {
      resizeObserver.disconnect()
    }
  }, [])

  // Notify parent of initial positions
  useEffect(() => {
    if (containerDimensions.width > 0 && containerDimensions.height > 0) {
      onPositionChange?.({
        namePosition,
        descriptionPosition,
        sponsorLogoPosition,
        linkPosition,
      })
    }
  }, [containerDimensions.width, containerDimensions.height])

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, delta } = event
      const { width, height } = containerDimensions

      if (width === 0 || height === 0) return

      // Convert delta from pixels to percentage
      const deltaPercentage = {
        x: (delta.x / width) * 100,
        y: (delta.y / height) * 100,
      }

      if (active.id === 'title') {
        const newPosition = {
          x: namePosition.x + deltaPercentage.x,
          y: namePosition.y + deltaPercentage.y,
        }
        setNamePosition(newPosition)
        onPositionChange?.({
          namePosition: newPosition,
          descriptionPosition,
          sponsorLogoPosition,
          linkPosition,
        })
      } else if (active.id === 'description') {
        const newPosition = {
          x: descriptionPosition.x + deltaPercentage.x,
          y: descriptionPosition.y + deltaPercentage.y,
        }
        setDescriptionPosition(newPosition)
        onPositionChange?.({
          namePosition,
          descriptionPosition: newPosition,
          sponsorLogoPosition,
          linkPosition,
        })
      } else if (active.id === 'logo') {
        const newPosition = {
          x: sponsorLogoPosition.x + deltaPercentage.x,
          y: sponsorLogoPosition.y + deltaPercentage.y,
        }
        setSponsorLogoPosition(newPosition)
        onPositionChange?.({
          namePosition,
          descriptionPosition,
          sponsorLogoPosition: newPosition,
          linkPosition,
        })
      } else if (active.id === 'button') {
        const newPosition = {
          x: linkPosition.x + deltaPercentage.x,
          y: linkPosition.y + deltaPercentage.y,
        }
        setLinkPosition(newPosition)
        onPositionChange?.({
          namePosition,
          descriptionPosition,
          sponsorLogoPosition,
          linkPosition: newPosition,
        })
      }
    },
    [
      containerDimensions,
      namePosition,
      descriptionPosition,
      sponsorLogoPosition,
      linkPosition,
      onPositionChange,
    ]
  )

  const { width, height } = containerDimensions
  const pixelPositions = {
    namePosition:
      width > 0 && height > 0
        ? percentageToPixels(namePosition, width, height)
        : namePosition,
    descriptionPosition:
      width > 0 && height > 0
        ? percentageToPixels(descriptionPosition, width, height)
        : descriptionPosition,
    sponsorLogoPosition:
      width > 0 && height > 0
        ? percentageToPixels(sponsorLogoPosition, width, height)
        : sponsorLogoPosition,
    linkPosition:
      width > 0 && height > 0
        ? percentageToPixels(linkPosition, width, height)
        : linkPosition,
  }

  return {
    containerRef,
    namePosition: pixelPositions.namePosition,
    descriptionPosition: pixelPositions.descriptionPosition,
    sponsorLogoPosition: pixelPositions.sponsorLogoPosition,
    linkPosition: pixelPositions.linkPosition,
    handleDragEnd,
  }
}
